import { useState } from "react";
import { motion } from "framer-motion";
import {
  User,
  Mail,
  Building,
  MessageSquare,
  Send,
  CheckCircle,
  Briefcase,
  Users,
  BarChart3,
  GraduationCap,
  Award,
  BookOpen,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/App";

const services = [
  {
    icon: BarChart3,
    title: "District PPS System Audit",
    description: "Comprehensive review of your district's current PPS systems, processes, and data to identify gaps, inconsistencies, and opportunities for aligned improvement.",
  },
  {
    icon: Users,
    title: "Coherence Assessment",
    description: "Evaluate alignment across buildings and teams to identify where variability creates risk or undermines student support outcomes.",
  },
  {
    icon: GraduationCap,
    title: "Implementation Coaching",
    description: "Ongoing executive-level support as you implement new protocols and systems, with regular check-ins, problem-solving, and accountability.",
  },
  {
    icon: Briefcase,
    title: "Leadership Advisory",
    description: "Strategic guidance for superintendents, assistant superintendents, and PPS directors building defensible, student-centered support systems.",
  },
];

export default function About() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    role: "",
    message: "",
    service_interest: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.post("/contacts", formData);
      setSubmitted(true);
      toast.success("Message sent! We'll be in touch soon.");
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="about-page">
      {/* Header */}
      <section className="bg-slate-900 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <p className="label-uppercase text-orange-400 mb-4">About</p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="about-title">
              Building Better Systems for Student Support
            </h1>
            <p className="text-lg text-slate-300">
              The PPS PlayBook was created to help district leaders build coherent, 
              repeatable systems that ensure every student gets the support they need—
              regardless of which building they attend or which staff member they encounter.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Why Coherence Matters
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  Too often, PPS operates as a collection of well-intentioned but disconnected 
                  efforts. One building handles crises differently than another. Referral 
                  processes vary by who you ask. Data exists but doesn't drive decisions.
                </p>
                <p>
                  <strong className="text-slate-900">Coherent systems change this.</strong> When 
                  protocols are clear, roles are defined, and data is actionable, student 
                  support becomes predictable, equitable, and effective.
                </p>
                <p>
                  This isn't about adding more work—it's about building structures that make 
                  the work sustainable and the outcomes consistent.
                </p>
              </div>
            </div>
            <div className="bg-slate-50 p-8 rounded-sm">
              <h3 className="text-xl font-bold text-slate-900 mb-6">
                Core Beliefs
              </h3>
              <ul className="space-y-4">
                {[
                  "Protocols should clarify, not complicate",
                  "Data should inform, not overwhelm",
                  "Systems should support people, not replace judgment",
                  "Consistency enables equity",
                  "Good structures make good work sustainable",
                ].map((belief) => (
                  <li key={belief} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{belief}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Our Founder */}
      <section className="py-16 lg:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="label-uppercase text-orange-400 mb-2">Founder</p>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Meet Our Founder
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Leading the mission to ensure every child attends school regularly
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12 items-start">
            {/* Founder Image/Card */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-slate-800 border border-slate-700 rounded-sm p-8 text-center"
              >
                <div className="w-40 h-40 mx-auto mb-6 rounded-full overflow-hidden border-4 border-orange-500">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_pps-playbook/artifacts/utm44lrp_3237D899-3CD8-4698-A644-402E5E9CC1B2.JPG" 
                    alt="Dr. Franky L. Simmons Jr."
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold text-white mb-1">
                  Dr. Franky L. Simmons Jr.
                </h3>
                <p className="text-orange-400 font-medium mb-6">Founder</p>
                
                <div className="space-y-3 text-left">
                  <div className="flex items-center gap-3 text-slate-300">
                    <Award className="w-5 h-5 text-orange-400 flex-shrink-0" />
                    <span className="text-sm">Ed.D., Russell Sage College</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300">
                    <Clock className="w-5 h-5 text-orange-400 flex-shrink-0" />
                    <span className="text-sm">20+ Years in Education</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300">
                    <BookOpen className="w-5 h-5 text-orange-400 flex-shrink-0" />
                    <span className="text-sm">Research-Based Practitioner</span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Founder Bio */}
            <div className="lg:col-span-2 space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <p className="text-slate-300 leading-relaxed">
                  Dr. Franky L. Simmons Jr. is a dedicated educator, researcher, and district leader whose work 
                  centers on strengthening student support systems and promoting equitable access to learning 
                  in public schools nationwide. With more than two decades of experience spanning classroom 
                  teaching, school leadership, and district-level innovation, Dr. Simmons brings a thoughtful 
                  blend of compassion, systems thinking, and practical problem-solving to some of education's 
                  most complex challenges.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <p className="text-slate-300 leading-relaxed">
                  Dr. Simmons earned his Doctorate in Education from Russell Sage College, where his research 
                  focused on the strategies and interventions school and district leaders use to address chronic 
                  absenteeism and related barriers to student engagement. His work highlights the interconnected 
                  roles of attendance, mental health, family engagement, and coordinated services, emphasizing 
                  the importance of coherent Pupil Personnel Services systems in supporting student success.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <p className="text-slate-300 leading-relaxed">
                  Through PPS Playbook, Dr. Simmons translates research and real-world leadership experience 
                  into actionable districtwide structures. The Playbook provides clear protocols, data guidance, 
                  and practical tools that help districts align Pupil Personnel Services practices across 
                  buildings, support professional judgment, and ensure students receive consistent, high-quality 
                  support. Each resource is designed to integrate seamlessly into existing systems, enabling 
                  leaders to move from reactive responses to intentional, equitable, and sustainable student support.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-slate-800 border-l-4 border-orange-500 p-6 rounded-r-sm"
              >
                <p className="text-white italic">
                  "Every student deserves consistent access to school and the supports that make learning possible. 
                  Strong Pupil Personnel Services systems ensure that attendance, well-being, and student success 
                  are addressed early, consistently, and equitably, so every student has the opportunity to reach 
                  their full potential."
                </p>
                <p className="text-orange-400 mt-3 font-medium">— Dr. Franky L. Simmons Jr.</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
